/**
 * GrowthChart — SVG area chart with brass quarterly markers.
 * Ported from charts.jsx (GrowthChart component) in the Claude Design output.
 *
 * Phase 3 Task 3.3 (2026-05-17): decoupled from the fake-data/anna
 * `GrowthPoint` type — re-exported locally so this chart has no production
 * dependency on the fixture module.
 *
 * 2026-06-10 (XCUT-07 / DASH-01): the fabricated benchmark series + its
 * dashed comparison line were removed. There is no market-data feed wired,
 * so the only series rendered is the trust's real AUM-snapshot history.
 * `range` is constrained upstream to windows the snapshot data covers.
 */

import { useState, useMemo, useRef } from 'react';

export interface GrowthPoint {
  month: string;
  value: number;
  q: boolean;
  qLabel?: string;
  qAmt?: number;
}

interface GrowthChartProps {
  data: GrowthPoint[];
  range: string;
  /** Extra y-domain headroom above the series max, as a fraction of the value
   *  span (day-one synthetic line, 2026-07-15): the default (absent) keeps the
   *  long-standing real-series composition; the synthetic zero-to-value line
   *  passes ~0.45 so the current value lands comfortably in the upper-middle
   *  of the plot instead of kissing the top edge. */
  headroom?: number;
  /** Optional quiet annotation on the point whose `month` matches — a brass
   *  dot + small label (e.g. "First valuation · Jul 2026"). Used by the
   *  day-one synthetic line to keep the ramp honest: it names the single real
   *  valuation date so nobody reads it as 12 months of history. */
  annotation?: { month: string; label: string };
}

const W = 760;
const H = 280;
// Y-axis labels sit on the RIGHT (2026-06-24, Paul): on the dashboard the
// chart is beside the big total-value figure, and a left axis crowded it.
const PAD = { t: 36, r: 66, b: 28, l: 22 };
const innerW = W - PAD.l - PAD.r;
const innerH = H - PAD.t - PAD.b;

function fmt(v: number): string {
  return 'R ' + (v / 1_000_000).toFixed(2).replace('.', ',') + 'm';
}

function fmtFull(v: number): string {
  return 'R ' + Math.round(v).toLocaleString('en-ZA').replace(/,/g, ' ');
}

/** Catmull-Rom → cubic-Bézier smoothing so the line reads as a smooth curve
 *  through the real monthly marks. Display-only: the curve passes through every
 *  snapshot point, so no data is invented between marks. */
function smoothPath(p: Array<{ x: number; y: number }>): string {
  if (p.length === 0) return '';
  if (p.length === 1) return `M ${p[0]!.x} ${p[0]!.y}`;
  let d = `M ${p[0]!.x.toFixed(2)} ${p[0]!.y.toFixed(2)}`;
  for (let i = 0; i < p.length - 1; i++) {
    const p1 = p[i]!;
    const p2 = p[i + 1]!;
    const p0 = p[i - 1] ?? p1;
    const p3 = p[i + 2] ?? p2;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return d;
}

/**
 * The chart's y-domain: 18% of the value span padded below the min; above the
 * max, `headroom` × span (default 0.108 — exactly the long-standing
 * `yPad * 0.6` composition, so real-series rendering is unchanged). A flat or
 * single-value series (span 0) falls back to the value itself so the domain
 * never collapses to zero height (division-by-zero NaN guard). Exported pure
 * for unit tests (day-one ruling: "current value comfortably in the
 * upper-middle, not kissing the top edge").
 */
export function computeYDomain(
  values: number[],
  headroom?: number,
): { yMin: number; yMax: number } {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || Math.abs(max) || 1;
  return {
    yMin: min - span * 0.18,
    yMax: max + span * (headroom ?? 0.108),
  };
}

export function GrowthChart({ data, range, headroom, annotation }: GrowthChartProps) {
  const [hover, setHover] = useState<{ i: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const sliced = useMemo<GrowthPoint[]>(() => {
    // Ranges are limited to windows the snapshot series genuinely covers:
    // 1M/3M/6M trail the last 2/4/7 monthly marks; 1Y is the full series
    // (requested with limit: 24). Anything wider would re-render the same
    // data under a misleading label, so it isn't offered. (The day-one
    // synthetic series is always 2 points, so every slice is a no-op there —
    // the hero rebuilds it per range instead.)
    if (range === '1M') return data.slice(-2);
    if (range === '3M') return data.slice(-4);
    if (range === '6M') return data.slice(-7);
    return data;
  }, [data, range]);

  const values = sliced.map((d) => d.value);
  const { yMin, yMax } = computeYDomain(values, headroom);

  const xFn = (i: number) =>
    PAD.l + (sliced.length === 1 ? innerW / 2 : (i / (sliced.length - 1)) * innerW);
  const yFn = (v: number) =>
    PAD.t + innerH - ((v - yMin) / (yMax - yMin)) * innerH;

  const linePts = sliced.map((d, i) => ({ x: xFn(i), y: yFn(d.value) }));
  const path = smoothPath(linePts);
  const areaPath = `${path} L ${xFn(sliced.length - 1)} ${PAD.t + innerH} L ${xFn(0)} ${PAD.t + innerH} Z`;

  const yTicks = useMemo<number[]>(() => {
    const ticks: number[] = [];
    const rangeVal = yMax - yMin;
    const step =
      Math.pow(10, Math.floor(Math.log10(rangeVal))) *
      (rangeVal / Math.pow(10, Math.floor(Math.log10(rangeVal))) > 5 ? 1 : 0.5);
    const start = Math.ceil(yMin / step) * step;
    for (let v = start; v < yMax; v += step) ticks.push(v);
    return ticks.slice(0, 4);
  }, [yMin, yMax]);

  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    if (px < PAD.l || px > W - PAD.r) { setHover(null); return; }
    const t = (px - PAD.l) / innerW;
    const i = Math.round(t * (sliced.length - 1));
    setHover({ i: Math.max(0, Math.min(sliced.length - 1, i)) });
  }

  const hoverPoint = hover ? sliced[hover.i] : null;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%', display: 'block' }}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="var(--primary-600)" stopOpacity="0.18" />
            <stop offset="60%"  stopColor="var(--primary-600)" stopOpacity="0.06" />
            <stop offset="100%" stopColor="var(--primary-600)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Y gridlines */}
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={PAD.l} x2={W - PAD.r} y1={yFn(v)} y2={yFn(v)} stroke="var(--hairline)" strokeWidth="1" />
            <text
              x={W - PAD.r + 12}
              y={yFn(v) + 4}
              fontSize="11"
              fill="var(--ink-3)"
              textAnchor="start"
              fontFamily="var(--mono)"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {fmt(v)}
            </text>
          </g>
        ))}

        {/* Area fill */}
        <path d={areaPath} fill="url(#areaFill)" />
        {/* Line */}
        <path d={path} fill="none" stroke="var(--primary-600)" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />

        {/* X axis labels */}
        {sliced.map((d, i) => {
          const show = sliced.length <= 7 || i % 2 === 0 || i === sliced.length - 1;
          if (!show) return null;
          return (
            <text key={i} x={xFn(i)} y={H - 8} fontSize="11" fill="var(--ink-3)" textAnchor="middle" fontFamily="var(--sans)">
              {d.month}
            </text>
          );
        })}

        {/* Quarterly distribution markers */}
        {sliced.map((d, i) => {
          if (!d.q) return null;
          return (
            <g key={`q-${i}`}>
              <line x1={xFn(i)} x2={xFn(i)} y1={PAD.t - 6} y2={yFn(d.value) - 6} stroke="var(--accent-400)" strokeWidth="1" strokeDasharray="2 3" opacity="0.6" />
              <g transform={`translate(${xFn(i)}, ${PAD.t - 14})`}>
                <rect x="-18" y="-10" width="36" height="18" rx="9" fill="var(--accent-100)" stroke="var(--accent-300)" strokeWidth="0.75" />
                <text x="0" y="3" fontSize="10" fill="var(--accent-700)" textAnchor="middle" fontWeight="600" fontFamily="var(--sans)" letterSpacing="0.04em">
                  {d.qLabel}
                </text>
              </g>
              <circle cx={xFn(i)} cy={yFn(d.value)} r="4.5" fill="var(--accent-400)" stroke="var(--surface)" strokeWidth="2" />
            </g>
          );
        })}

        {/* Annotated point (day-one line): a brass dot on the matching month +
            a quiet label, anchored to the left of the point so it never clips
            on the right edge. Same dot idiom as the quarterly markers. */}
        {annotation && (() => {
          const ai = sliced.findIndex((d) => d.month === annotation.month);
          if (ai === -1) return null;
          const ax = xFn(ai);
          const ay = yFn(sliced[ai]!.value);
          return (
            <g data-testid="growth-annotation">
              <circle cx={ax} cy={ay} r="4.5" fill="var(--accent-400)" stroke="var(--surface)" strokeWidth="2" />
              <text
                x={ax - 12}
                y={ay - 12}
                fontSize="11"
                fill="var(--ink-3)"
                textAnchor="end"
                fontFamily="var(--sans)"
              >
                {annotation.label}
              </text>
            </g>
          );
        })()}

        {/* Hover cursor */}
        {hover && hoverPoint && (
          <g>
            <line x1={xFn(hover.i)} x2={xFn(hover.i)} y1={PAD.t} y2={PAD.t + innerH} stroke="var(--ink-3)" strokeWidth="1" strokeDasharray="2 3" opacity="0.5" />
            <circle cx={xFn(hover.i)} cy={yFn(hoverPoint.value)} r="5" fill="var(--surface)" stroke="var(--primary-600)" strokeWidth="2" />
          </g>
        )}
      </svg>

      {/* Tooltip */}
      {hover && hoverPoint && (() => {
        const cx = (xFn(hover.i) / W) * 100;
        const cy = (yFn(hoverPoint.value) / H) * 100;
        return (
          <div className="tt" style={{ left: `${cx}%`, top: `${cy}%` }}>
            <div className="eyebrow on-dark" style={{ marginBottom: 4 }}>{hoverPoint.month}</div>
            <div><b>{fmtFull(hoverPoint.value)}</b></div>
            {hoverPoint.q && (
              <div style={{ marginTop: 4, color: 'var(--accent-200)' }}>
                + {hoverPoint.qLabel} distribution &middot; {fmtFull(hoverPoint.qAmt ?? 0)}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
